import type { VisitOptions } from "@inertiajs/core"
import { useCallback, useState } from "react"
import { toast } from "sonner"
import { AgreementConfirmDialog } from "@/components/agreements/agreement-confirm-dialog"
import { useTranslation } from "@/hooks/use-translation"
import { runWorkspace } from "@/lib/inertia-actions"
import { checkSeedanceVideoConsent, type CheckSeedanceVideoConsentResponse } from "@/lib/workspace-api"

type SeedanceVideoGenerationRequest = {
  shotIds: number[]
  onStart?: (shotIds: number[]) => void
  visitOptions?: Partial<VisitOptions>
  /** One-shot resolution for this run (e.g. first-gen min resolution); does not change shot settings. */
  videoResolutionOverride?: string | null
}

type PendingSeedanceVideoConsent = {
  consent: CheckSeedanceVideoConsentResponse
  onStart?: (shotIds: number[]) => void
  visitOptions?: Partial<VisitOptions>
  videoResolutionOverride?: string | null
}

export function useSeedanceVideoConsent(projectId: number) {
  const { t } = useTranslation(["workspace"])
  const [pendingConsent, setPendingConsent] = useState<PendingSeedanceVideoConsent | null>(null)

  const runVideoGeneration = useCallback(
    (
      shotIds: number[],
      onStart?: (shotIds: number[]) => void,
      visitOptions?: Partial<VisitOptions>,
      videoResolutionOverride?: string | null,
    ) => {
      onStart?.(shotIds)
      const extra: Record<string, unknown> = shotIds.length > 0 ? { shot_ids: shotIds } : {}
      if (videoResolutionOverride) {
        extra.video_resolution_override = videoResolutionOverride
      }
      runWorkspace(projectId, "generate_video", extra, visitOptions)
    },
    [projectId],
  )

  const requestSeedanceVideoGeneration = useCallback(
    async ({ shotIds, onStart, visitOptions, videoResolutionOverride }: SeedanceVideoGenerationRequest) => {
      try {
        const consent = await checkSeedanceVideoConsent(projectId, shotIds)
        if (consent.needs_consent) {
          setPendingConsent({ consent, onStart, visitOptions, videoResolutionOverride })
          return
        }

        runVideoGeneration(shotIds, onStart, visitOptions, videoResolutionOverride)
      } catch (e) {
        toast.error(e instanceof Error ? e.message : t("seedance.videoConsentCheckFailed"))
      }
    },
    [projectId, runVideoGeneration, t],
  )

  const handleConfirmSeedanceVideoConsent = useCallback(
    (version: number) => {
      if (!pendingConsent) return

      const shotIds = pendingConsent.consent.shot_ids
      pendingConsent.onStart?.(shotIds)
      const extra: Record<string, unknown> = {
        shot_ids: shotIds,
        agreement_version: version,
      }
      if (pendingConsent.videoResolutionOverride) {
        extra.video_resolution_override = pendingConsent.videoResolutionOverride
      }
      setPendingConsent(null)
      runWorkspace(projectId, "register_seedance_video_consent", extra, pendingConsent.visitOptions)
    },
    [pendingConsent, projectId],
  )

  const seedanceVideoConsentDialog = (
    <AgreementConfirmDialog
      open={pendingConsent !== null}
      onOpenChange={(open) => {
        if (!open) setPendingConsent(null)
      }}
      agreementKey={pendingConsent?.consent.agreement_key ?? "seedance_avatar_registration"}
      intro={t("seedance.videoConsentIntro", {
        names: pendingConsent?.consent.owners.map((owner) => owner.label).join(", ") ?? "",
      })}
      onConfirm={handleConfirmSeedanceVideoConsent}
    />
  )

  return { requestSeedanceVideoGeneration, seedanceVideoConsentDialog }
}
